import { Button } from '@/components/ui/button'
import CharacterCount from '@/components/form/CharacterCount'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'
import { formatPhone } from '@/hooks/use-phone-formatter'
import dayjs from '@/lib/dayjs'
import { useAppForm } from '@/lib/form'
import { ORDER_FIELD_MAX_LENGTHS, ORDER_FIELD_NATIVE_MAX_LENGTHS } from '@/lib/order-field-limits'
import { pickupFormSchema } from '@/lib/services/deliveries/deliveries.schema'
import { getErrorMessage, toYmdFromDate } from '@/lib/utils'
import {
  getPosFulfillmentDateBlockKind,
  getPosFulfillmentDateBlockTooltip,
  getPosFulfillmentDateWarning,
  getPosFulfillmentDateAvailability,
} from '@/lib/pos/fulfillment-availability'
import { usePosFulfillmentAvailabilityNow } from './usePosFulfillmentAvailabilityNow'
import {
  getPosSubscriptionScheduleDefaults,
  PosSubscriptionScheduleField,
} from './PosSubscriptionScheduleField'
import { AlertTriangle } from 'lucide-react'
import { useMemo } from 'react'
import { toast } from 'sonner'

function trimContactValue(value) {
  return String(value ?? '').trim()
}

function normalizeComparableContactValue(value, { email = false } = {}) {
  const trimmedValue = trimContactValue(value)

  return email ? trimmedValue.toLowerCase() : trimmedValue
}

function getCustomerPickupSnapshot(cart) {
  const customer = cart?.purchaser ?? null

  return {
    name: trimContactValue(customer?.name),
    phone: trimContactValue(customer?.phone),
    email: trimContactValue(customer?.email),
  }
}

function getExistingPickupSnapshot(cart) {
  return {
    name: trimContactValue(cart?.recipient_name),
    phone: trimContactValue(cart?.recipient_phone),
    email: trimContactValue(cart?.recipient_email),
  }
}

function hasDifferentExistingPickupPerson(
  cart,
  customerSnapshot = getCustomerPickupSnapshot(cart)
) {
  const existingSnapshot = getExistingPickupSnapshot(cart)
  const contactPairs = [
    [existingSnapshot.name, customerSnapshot.name, {}],
    [existingSnapshot.phone, customerSnapshot.phone, {}],
    [existingSnapshot.email, customerSnapshot.email, { email: true }],
  ]

  return contactPairs.some(([existingValue, customerValue, options]) => {
    if (!existingValue) {
      return false
    }

    return (
      normalizeComparableContactValue(existingValue, options) !==
      normalizeComparableContactValue(customerValue, options)
    )
  })
}

function getManualPickupSnapshot(cart, customerSnapshot) {
  if (hasDifferentExistingPickupPerson(cart, customerSnapshot)) {
    return getExistingPickupSnapshot(cart)
  }

  return {
    name: '',
    phone: '',
    email: '',
  }
}

export function getPickupFormDefaults(cart) {
  const customerSnapshot = getCustomerPickupSnapshot(cart)
  const customerCanPickUp = Boolean(customerSnapshot.name)
  const customerWillPickUp =
    customerCanPickUp && !hasDifferentExistingPickupPerson(cart, customerSnapshot)
  const pickupSnapshot = customerWillPickUp ? customerSnapshot : getExistingPickupSnapshot(cart)

  return {
    pickupDate: cart?.fulfillment_due_date
      ? dayjs.utc(cart.fulfillment_due_date).format('YYYY-MM-DD')
      : '',
    preferredWeekday: getPosSubscriptionScheduleDefaults(cart).preferredWeekday,
    customerWillPickUp,
    recipientName: pickupSnapshot.name,
    recipientPhone: pickupSnapshot.phone,
    recipientEmail: pickupSnapshot.email,
    specialInstructions: cart?.special_instructions || '',
  }
}

export function PickupModal({ open, onOpenChange, location, onConfirm, cart }) {
  const availabilityNow = usePosFulfillmentAvailabilityNow(open)
  const pickupDateAvailability = useMemo(() => {
    if (!open || !(location?.is_pickup || location?.fulfillment_windows)) {
      return null
    }

    return getPosFulfillmentDateAvailability(location, {
      isPickup: true,
      now: availabilityNow,
    })
  }, [availabilityNow, location, open])
  const form = useAppForm({
    defaultValues: getPickupFormDefaults(cart),
    validators: {
      onSubmit: pickupFormSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (onConfirm) {
          try {
            const customerSnapshot = getCustomerPickupSnapshot(cart)
            const pickupSnapshot =
              value.customerWillPickUp && customerSnapshot.name
                ? customerSnapshot
                : {
                    name: value.recipientName,
                    phone: value.recipientPhone,
                    email: value.recipientEmail,
                  }

            await onConfirm({
              date: value.pickupDate,
              ...(value.preferredWeekday ? { preferredWeekday: value.preferredWeekday } : {}),
              recipientName: pickupSnapshot.name,
              recipientPhone: pickupSnapshot.phone,
              recipientEmail: pickupSnapshot.email,
              specialInstructions: value.specialInstructions,
            })
            formApi.reset()
            onOpenChange(false)
          } catch (error) {
            toast.error('Failed to save pickup information', {
              description: getErrorMessage(error),
            })
            if (error?.response?.data?.errors) {
              return { fields: error.response.data.errors }
            }
          }
        } else {
          formApi.reset()
          onOpenChange(false)
        }
      },
    },
  })

  const handleClose = () => {
    form.reset(getPickupFormDefaults(cart))
    onOpenChange(false)
  }

  const handleDialogOpenChange = (isOpen) => {
    if (!isOpen) {
      handleClose()
    }
  }

  const customerSnapshot = getCustomerPickupSnapshot(cart)
  const hasCustomerPickupSnapshot = Boolean(customerSnapshot.name)
  const customerPickupSummary = [
    customerSnapshot.name,
    customerSnapshot.phone ? formatPhone(customerSnapshot.phone) : null,
    customerSnapshot.email,
  ]
    .filter(Boolean)
    .join(' | ')

  const handleCustomerWillPickUpChange = (field, isChecked) => {
    const customerWillPickUp = Boolean(isChecked)
    field.handleChange(customerWillPickUp)

    const pickupSnapshot =
      customerWillPickUp && hasCustomerPickupSnapshot
        ? customerSnapshot
        : getManualPickupSnapshot(cart, customerSnapshot)

    form.setFieldValue('recipientName', pickupSnapshot.name)
    form.setFieldValue('recipientPhone', pickupSnapshot.phone)
    form.setFieldValue('recipientEmail', pickupSnapshot.email)
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Select Pickup Date</DialogTitle>
          <DialogDescription className="sr-only">
            Select a pickup date, pickup person, and optional pickup instructions.
          </DialogDescription>
        </DialogHeader>

        <form.AppForm>
          <form.Form>
            <div className="space-y-4 mt-4">
              <div className="space-y-4">
                <form.AppField
                  name="pickupDate"
                  listeners={{
                    onChange: ({ value }) => {
                      if (!form.getFieldValue('preferredWeekday')) {
                        form.setFieldValue(
                          'preferredWeekday',
                          getPosSubscriptionScheduleDefaults({
                            ...cart,
                            fulfillment_due_date: value,
                          }).preferredWeekday
                        )
                      }
                    },
                  }}
                >
                  {(field) => (
                    <field.DatePicker
                      label="Pickup Date"
                      minDate={
                        pickupDateAvailability
                          ? dayjs(pickupDateAvailability.min).toDate()
                          : dayjs().toDate()
                      }
                      maxDate={
                        pickupDateAvailability ? dayjs(pickupDateAvailability.max).toDate() : null
                      }
                      disabled={(date) =>
                        pickupDateAvailability?.hardDisabledDates?.has(toYmdFromDate(date)) ?? false
                      }
                      modifiers={{
                        availabilityClosed: (date) =>
                          getPosFulfillmentDateBlockKind(
                            pickupDateAvailability,
                            toYmdFromDate(date)
                          ) === 'closed',
                        availabilityUnavailable: (date) =>
                          getPosFulfillmentDateBlockKind(
                            pickupDateAvailability,
                            toYmdFromDate(date)
                          ) === 'unavailable',
                      }}
                      availabilityBlockLabel={(date) =>
                        getPosFulfillmentDateBlockTooltip(
                          pickupDateAvailability,
                          toYmdFromDate(date),
                          { isPickup: true }
                        )
                      }
                      className="w-full"
                    />
                  )}
                </form.AppField>

                <form.Subscribe selector={(state) => state.values.pickupDate}>
                  {(pickupDate) => {
                    const dateWarning = getPosFulfillmentDateWarning(
                      pickupDateAvailability,
                      pickupDate,
                      { isPickup: true }
                    )

                    return dateWarning ? (
                      <Alert variant="warning">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>{dateWarning.title}</AlertTitle>
                        <AlertDescription>{dateWarning.description}</AlertDescription>
                      </Alert>
                    ) : null
                  }}
                </form.Subscribe>

                <PosSubscriptionScheduleField
                  form={form}
                  cart={cart}
                  dateFieldName="pickupDate"
                  fulfillmentType="pickup"
                />
              </div>

              <div className="space-y-2 pt-4 border-t">
                <form.AppField name="specialInstructions">
                  {(field) => (
                    <div className="space-y-1">
                      <field.TextareaInput
                        label="Special Instructions (Optional)"
                        placeholder="Pickup instructions, etc."
                        maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.special_instructions}
                        characterMaxLength={ORDER_FIELD_MAX_LENGTHS.special_instructions}
                        rows={3}
                      />
                      <CharacterCount
                        value={field.state.value}
                        maxLength={ORDER_FIELD_MAX_LENGTHS.special_instructions}
                      />
                    </div>
                  )}
                </form.AppField>
              </div>

              <form.Subscribe selector={(state) => Boolean(state.values.customerWillPickUp)}>
                {(customerWillPickUp) => (
                  <div className="space-y-4 pt-4 border-t">
                    {hasCustomerPickupSnapshot ? (
                      <form.AppField name="customerWillPickUp">
                        {(field) => (
                          <div className="rounded-lg border bg-muted/30 p-3">
                            <div className="flex items-start gap-3">
                              <Checkbox
                                id="pickup-customer-will-pick-up"
                                type="button"
                                checked={Boolean(field.state.value)}
                                onCheckedChange={(isChecked) =>
                                  handleCustomerWillPickUpChange(field, isChecked)
                                }
                              />
                              <div className="min-w-0 space-y-1">
                                <Label htmlFor="pickup-customer-will-pick-up" appearance="choice">
                                  Customer will pick up
                                </Label>
                                {field.state.value ? (
                                  <Typography variant="label" tone="muted" className="block">
                                    Using {customerPickupSummary}
                                  </Typography>
                                ) : (
                                  <Typography variant="label" tone="muted" className="block">
                                    Enter a different pickup person below.
                                  </Typography>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </form.AppField>
                    ) : null}

                    {!customerWillPickUp ? (
                      <div className="space-y-4">
                        <form.AppField name="recipientName">
                          {(field) => (
                            <field.TextInput
                              id="pickup-recipient-name"
                              label="Pickup Person"
                              placeholder="Who is picking this up?"
                              maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.recipient_name}
                              characterMaxLength={ORDER_FIELD_MAX_LENGTHS.recipient_name}
                            />
                          )}
                        </form.AppField>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <form.AppField name="recipientPhone">
                            {(field) => (
                              <field.PhoneInput
                                id="pickup-recipient-phone"
                                label="Phone (Optional)"
                                maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.recipient_phone}
                                characterMaxLength={ORDER_FIELD_MAX_LENGTHS.recipient_phone}
                              />
                            )}
                          </form.AppField>

                          <form.AppField name="recipientEmail">
                            {(field) => (
                              <field.EmailInput
                                id="pickup-recipient-email"
                                label="Email (Optional)"
                                placeholder="pickup@example.com"
                                maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.recipient_email}
                                characterMaxLength={ORDER_FIELD_MAX_LENGTHS.recipient_email}
                              />
                            )}
                          </form.AppField>
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </form.Subscribe>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={handleClose} type="button">
                Cancel
              </Button>
              <form.SubmitButton className="w-auto">Confirm</form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  )
}
